import React, { useEffect, useState } from "react";
import { Col, Container, Modal, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import CommonTitleBar from "components/common/CommonTitleBar";
import { useDispatch, useSelector } from "react-redux";
import {
  cloneCoupon,
  deleteCoupon,
  getAllCoupons,
  updateCouponStatus,
} from "store/actions/couponAction";
import Spinners from "components/common/Spinners";
import NoDataFound from "components/common/NoDataFound";
import { Dropdown } from "react-bootstrap";
import { Ellipsis, X } from "lucide-react";
import {
  formatDate,
  getMinMaxDate,
  handleStopPropagation,
} from "components/common/Utils";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import { isAfter, parseISO } from "date-fns";
import {
  getBranchList,
  getBranchListWithCity,
} from "store/actions/branchSlice";
import Paginations from "components/common/Paginations";
import { showAlert } from "store/reducers/alertReducer";
import Filters from "components/common/Filters";
import { statusOptions } from "components/constants/StatusKeys";
import {
  orderSpaceType,
  spaceTypeOptions,
} from "components/constants/DropdownOptions";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import Riyal from "assets/images/icons/riyal.svg";

const Coupons = () => {
  const { t } = useTranslation();
  document.title = t("Common | Da Nonna");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    coupons,
  } = useSelector((state) => state.Coupons);

  const { branchesList } = useSelector((state) => state.Branches);
  const [dates, setDates] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    status: -1,
    orderBy: -1,
    branches: [],
    spaceTypes: [],
    type: "",
    discountType: "",
    discountValue: null,
    totalUsedMin: null,
    totalUsedMax: null,
    totalUsedLimitMin: null,
    totalUsedLimitMax: null,
    totalRedeemedMin: null,
    totalRedeemedMax: null,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false); // DELETE MODAL
  const [show, setShow] = useState(false);
  const [newCode, setNewCode] = useState("");

  useEffect(() => {
    const fetchBranchesList = () => {
      dispatch(getBranchList());
    };
    fetchBranchesList();
    dispatch(getBranchListWithCity());
    getAllData();
  }, []);

  const handleModalClose = () => {
    setNewCode("");
    setShow(false);
    setSelectedId(null);
  };
  const handleCloseDelete = () => {
    setShowModal(false);
    setSelectedId(null);
  };

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const {
      orderBy,
      status,
      branches,
      type,
      discountType,
      discountValue,
      spaceTypes,
      totalUsedMin,
      totalUsedMax,
      totalUsedLimitMin,
      totalUsedLimitMax,
      totalRedeemedMin,
      totalRedeemedMax,
    } = selectedFilters;

    const isReset = actionType === "RESET";

    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      type: isReset ? "" : type || "", // "private","public"
      dateRange: isReset ? {} : getMinMaxDate(dates),
      discountType: isReset ? "" : discountType, // "fixed","percentage"
      discountValue: isReset ? null : discountValue,
      branches: isReset ? [] : branches,
      spaceTypes: isReset ? [] : spaceTypes, // "workSpace", "workShop", "meetingRoom"
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
      maxUsageOfCustomersRange: isReset
        ? {}
        : {
            min: totalUsedMin,
            max: totalUsedMax,
          },
      maxUsagePerCustomerRange: isReset
        ? {}
        : {
            min: totalUsedLimitMin,
            max: totalUsedLimitMax,
          },
      usageCountRange: isReset
        ? {}
        : {
            min: totalRedeemedMin,
            max: totalRedeemedMax,
          },
    };

    dispatch(getAllCoupons(payload));

    // navigate(`/rewards/coupons?page=${page || pageNumber}`);
  };

  const handleStatusChange = (id, status) => {
    setSelectedId(id);
    const payload = {
      id,
      status: status === 1 ? 0 : 1,
    };

    dispatch(updateCouponStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
          setSelectedId(null);
        }
      })
      .catch((err) => {});
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteCoupon(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleClone = () => {
    if (newCode.trim().length < 6) {
      dispatch(
        showAlert({
          text: "Coupon code must be at least 6 characters long.",
          type: "error",
        })
      );
      return;
    }

    const payload = {
      id: selectedId,
      newCode,
    };
    dispatch(cloneCoupon(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          handleModalClose();
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const getStatusDetails = (status, expiresAt, isNeverEnd) => {
    // const isExpired = isNeverEnd
    //   ? false
    //   : expiresAt && isAfter(new Date(), parseISO(expiresAt));

    // if (isExpired) {
    //   return { label: "Expired", className: "cancelled_status" }; // Example expired style
    // }

    const statusMap = {
      1: { label: "Active", className: "completed_status" },
      0: { label: "Deactive", className: "cancelled_status" },
    };

    return statusMap[status] ?? {};
  };

  const getBranchNames = (applicableBranches) => {
    return (
      branchesList
        ?.filter((branch) => applicableBranches.includes(branch._id))
        ?.map((branch) => branch.name)
        .join(", ") || t("No applicable branches")
    );
  };

  const getSpaceTypeLabel = (spaceType) => {
    const spaceTypeMap = {
      workSpace: t("Da Nonna"),
      meetingRoom: t("Meeting Room"),
      workShop: t("Workshop"),
    };

    return spaceTypeMap[spaceType] ?? "-";
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({
      status: -1,
      orderBy: -1,
      branches: [],
      spaceTypes: [],
      type: "",
      discountType: "",
      discountValue: null,
      totalUsedMin: null,
      totalUsedMax: null,
      totalUsedLimitMin: null,
      totalUsedLimitMax: null,
      totalRedeemedMin: null,
      totalRedeemedMax: null,
    });
    getAllData(1, { actionType: "RESET" });
  };

  const filters = [
    {
      key: "filterBy",
      placeholder: "Filter By",
      type: "advancedFilter",
      filters: [
        // Adding new filters for Coupons
        {
          name: "Access Type",
          key: "type",
          type: "buttons",
          options: [
            { name: "Public", value: "public" },
            { name: "Private", value: "private" },
          ],
        },
        {
          name: "Discount Type",
          key: "discountType",
          type: "buttons",
          options: [
            { name: "Percentage", value: "percentage" },
            { name: "Fixed", value: "fixed" },
          ],
        },
        {
          name: "",
          key: "discountValue",
          type: "number",
          min: 0,
          max: 1000,
        },
        {
          name: "Total Used",
          key: "totalUsed",
          type: "range",
          min: null,
          max: null,
          step: 10,
          defaultMin: 0,
          defaultMax: 1000,
        },
        {
          name: "Total Used Limit",
          key: "totalUsedLimit",
          type: "range",
          min: null,
          max: null,
          step: 10,
          defaultMin: 0,
          defaultMax: 1000,
        },
        {
          name: "Total Redeemed",
          key: "totalRedeemed",
          type: "range",
          min: null,
          max: null,
          step: 10,
          defaultMin: 0,
          defaultMax: 1000,
        },
      ],
    },

    {
      key: "spaceTypes",
      placeholder: "Space Type",
      options: spaceTypeOptions,
      type: "multiSelect",
    },
    {
      key: "status",
      placeholder: "Status",
      options: [
        { name: "Active", value: 1 },
        { name: "Expired", value: 0 },
      ],
      type: "singleSelect",
    },
    {
      key: "branches",
      type: "branchCityFilter",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Coupons")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => navigate("/rewards/coupons/add-new-coupon")}
          />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
              className="filter_2"
            />
            <Row className="mt-4">
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={10} />
                ) : coupons.length === 0 ? (
                  <NoDataFound />
                ) : (
                  <div
                    className="common_table_style table-responsive"
                    style={{ minHeight: showDropdown ? 300 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head full_th">
                        <tr>
                          <th>#</th>
                          <th>
                            <div className="d-flex align-items-center gap-1">
                              {t("Coupon Code")}
                            </div>
                          </th>
                          <th>{t("Access Type")}</th>
                          <th>{t("Discount Type")}</th>
                          <th>{t("Discount Value")}</th>
                          <th>{t("Total Used")}</th>
                          <th>{t("Start Date")}</th>
                          <th>{t("End Date")}</th>
                          <th>{t("Total Usage Limit")}</th>
                          <th>{t("Usage Per User")}</th>
                          <th>{t("Space Type")}</th>
                          <th>{t("Branches")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Total Redeemed")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coupons?.map((item, index) => (
                          <tr key={item._id}>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>{item?.code}</td>
                            <td>
                              <p
                                className={`text-capitalize ${
                                  item?.isPrivate
                                    ? "text_danger"
                                    : "text_primary"
                                }`}
                              >
                                {item?.isPrivate ? t("Private") : t("Public")}
                              </p>
                            </td>
                            <td>
                              <p className="text-capitalize">
                                {item?.discountType || "-"}
                              </p>
                            </td>
                            <td>
                              {item?.discountType !== "percentage" && (
                                <img
                                  src={Riyal}
                                  width={15}
                                  height={15}
                                  alt="riyal"
                                  className="me-1"
                                />
                              )}
                              {item?.amount || "-"}
                              {item?.discountType === "percentage" && "%"}
                            </td>
                            <td>{item?.usageCount ?? 0}</td>
                            <td>
                              <p className="w_fit_content">
                                {item?.isNeverEnd
                                  ? t("Active")
                                  : formatDate(item?.startsAt || "-")}
                              </p>
                            </td>
                            <td>
                              <p className="w_fit_content">
                                {item?.isNeverEnd
                                  ? t("Never Expires")
                                  : formatDate(item?.expiresAt || "-")}
                              </p>
                            </td>
                            <td>
                              {!item?.limits?.isMaxUsageOfCustomers
                                ? item?.limits?.maxUsageOfCustomers
                                : t("Unlimited")}
                            </td>

                            <td>
                              {!item?.limits?.isMaxUsagePerCustomer
                                ? item?.limits?.maxUsagePerCustomer
                                : t("Unlimited")}
                            </td>
                            <td>{getSpaceTypeLabel(item?.spaceType)}</td>
                            <td>
                              <p style={{ minWidth: 200 }}>
                                {getBranchNames(item?.applicableBranches)}
                              </p>
                            </td>
                            <td>
                              <div>
                                <p
                                  className={
                                    getStatusDetails(
                                      item?.status,
                                      item?.expiresAt,
                                      item?.isNeverEnd
                                    ).className
                                  }
                                >
                                  {
                                    getStatusDetails(
                                      item?.status,
                                      item?.expiresAt,
                                      item?.isNeverEnd
                                    ).label
                                  }
                                </p>
                                {item?.isActive === 0 && (
                                  <p className="cancelled_status mt-1">
                                    {t("Expired")}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td>{item?.usageCount ?? 0}</td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  autoClose="outside"
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        navigate(
                                          `/rewards/coupons/update-coupon?id=${item?._id}`
                                        );
                                      }}
                                    >
                                      {t("Edit Coupon")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        setSelectedId(item?._id);
                                        setShow(true);
                                      }}
                                    >
                                      {t("Duplicate")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      className={
                                        item.status === 1
                                          ? "text_danger"
                                          : "text_primary"
                                      }
                                      onClick={() => {
                                        handleStatusChange(
                                          item._id,
                                          item.status
                                        );
                                      }}
                                    >
                                      {item.status === 1
                                        ? t("Deactive")
                                        : t("Active")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      className="text_danger"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setShowModal(true);
                                      }}
                                    >
                                      {t("Delete")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
      <DeleteConfirmationModal
        show={showModal}
        onClose={handleCloseDelete}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "Coupon",
        })}
        isLoading={isLoading}
      />
      <Modal
        centered
        show={show}
        onHide={handleModalClose}
        className="common_modal"
      >
        <div className="modal_header g_between">
          <h2 className="fs_17">{t("Duplicate Coupon")}</h2>
          <X
            onClick={handleModalClose}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body mt-4">
          <input
            type="text"
            className="form-control"
            placeholder={t("Enter New Coupon Code")}
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
          />

          <button
            className="main_btn mt-3 ms-auto d-block"
            disabled={isLoading}
            onClick={handleClone}
          >
            {t("Save")}
          </button>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default Coupons;
